import { useEffect, useMemo, useRef, useState } from "react";
import Select, {
  type FormatOptionLabelMeta,
  type MultiValue,
} from "react-select";
import api from "../../../../services/api";
import type {
  AiSkillCatalogItem,
  Tenant,
  TenantCustomerDataDictionaryItem,
  TenantConversationFollowUp,
} from "../../../../types/tenant";
import {
  AiFunctionsField,
  normalizeAiFunctionOptions,
  normalizeAiFunctions,
  type AiFunctionOption,
} from "../../../../components/ai-functions-field";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import { useAuth } from "../../../../hooks/use-auth";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import {
  APP_RESOURCES,
  AccessControl,
} from "../../../../permissions/access-control";
import type { AiModel } from "../../../../types/ai-model";

const DEFAULT_TIMEOUT_MESSAGE =
  "Olá! Como não tivemos novas mensagens por aqui, vou encerrar esta conversa por enquanto. Se precisar de algo, é só me chamar novamente 😊";

const DEFAULT_FOLLOW_UP_MINUTES = 3;
type AiSkillOption = {
  value: string;
  label: string;
  description: string;
};

const DEFAULT_CUSTOMER_DATA_DICTIONARY: TenantCustomerDataDictionaryItem[] = [
  {
    key: "nome",
    label: "Nome do contato",
    description: "Nome do contato salvo no contexto.",
  },
];

type TemplateOption = {
  id: string;
  element_name: string;
  data?: string;
  params_count?: number;
};

type PromptSnippet = {
  label: string;
  snippet: string;
};
type ModelOption = {
  value: string;
  label: string;
};

type FindAllTenantDto = {
  data: Tenant[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

const BASE_PROMPT_TEMPLATE = `## Papel da IA
Você é um atendente virtual da empresa {{empresa}}.

## Objetivo
- Resolver solicitações com clareza e agilidade.
- Coletar informações essenciais antes de concluir o atendimento.

## Regras obrigatórias
- Não invente informações.
- Se faltar contexto, faça perguntas curtas e objetivas.
- Responda sempre em português do Brasil.

## Contexto de negócio
- Produtos e serviços principais:
- Horário de atendimento:
- Políticas importantes:

## Formato de resposta
- Use linguagem profissional e cordial.
- Prefira respostas em tópicos quando fizer sentido.
- Finalize com o próximo passo claro para o contato.
`;

const PROMPT_SNIPPETS: PromptSnippet[] = [
  { label: "Template base", snippet: `${BASE_PROMPT_TEMPLATE}\n` },
  {
    label: "Persona",
    snippet:
      "## Persona\nVocê é especialista em atendimento consultivo para o setor da empresa.\n\n",
  },
  {
    label: "Regras",
    snippet:
      "## Regras\n- Seja objetivo e educado.\n- Não use informações fora do contexto fornecido.\n\n",
  },
  {
    label: "Contexto",
    snippet:
      "## Contexto\n- Dados disponíveis no sistema:\n- Limitações operacionais:\n\n",
  },
  {
    label: "Formato",
    snippet:
      "## Formato da resposta\n1. Resuma a necessidade do contato.\n2. Traga a orientação.\n3. Confirme próximo passo.\n\n",
  },
];

const normalizeStringArray = (rawValues: unknown): string[] => {
  if (!Array.isArray(rawValues)) return [];

  const uniqueValues: string[] = [];
  const seen = new Set<string>();

  rawValues.forEach((value) => {
    if (typeof value !== "string") return;
    const normalized = value.trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    uniqueValues.push(normalized);
  });

  return uniqueValues;
};

const normalizeAiSkillOptions = (items: unknown): AiSkillOption[] => {
  if (!Array.isArray(items)) return [];

  const seen = new Set<string>();
  const options: AiSkillOption[] = [];

  items.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const record = item as Partial<AiSkillCatalogItem>;
    const slug = typeof record.slug === "string" ? record.slug.trim() : "";
    if (!slug || seen.has(slug)) return;

    seen.add(slug);
    options.push({
      value: slug,
      label:
        typeof record.name === "string" && record.name.trim().length > 0
          ? record.name.trim()
          : slug,
      description: Array.isArray(record.relatedIntents)
        ? record.relatedIntents
            .map((intent) => intent.name || intent.slug)
            .filter(Boolean)
            .join(", ")
        : "",
    });
  });

  return options;
};

const formatAiSkillOptionLabel = (
  option: AiSkillOption,
  meta: FormatOptionLabelMeta<AiSkillOption>,
) => (
  <div title={option.description || option.label} className="min-w-0">
    <span className="block truncate">{option.label}</span>
    {meta.context === "menu" && option.description && (
      <span className="mt-0.5 block whitespace-normal text-xs leading-snug text-slate-500">
        {option.description}
      </span>
    )}
  </div>
);

const normalizeCustomerDataDictionary = (
  dictionary: unknown,
): TenantCustomerDataDictionaryItem[] => {
  if (!Array.isArray(dictionary)) return [];

  const normalizedDictionary: TenantCustomerDataDictionaryItem[] = [];
  const seen = new Set<string>();

  dictionary.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const record = item as Partial<TenantCustomerDataDictionaryItem>;
    const key = typeof record.key === "string" ? record.key.trim() : "";
    if (!key || seen.has(key)) return;

    seen.add(key);
    normalizedDictionary.push({
      key,
      label:
        typeof record.label === "string" && record.label.trim().length > 0
          ? record.label.trim()
          : key,
      description:
        typeof record.description === "string" ? record.description.trim() : "",
    });
  });

  return normalizedDictionary;
};

type AiConfig = Pick<
  Tenant,
  | "prompt"
  | "gptModel"
  | "gptAudioModel"
  | "gptImageModel"
  | "gptFileModel"
  | "enabledAiAudioInterpretation"
  | "enabledAiImageInterpretation"
  | "enabledAiFileInterpretation"
  | "enabledAssistantMessageSplit"
  | "conversationTimeoutEnabled"
  | "conversationTimeoutMinutes"
  | "conversationTimeoutMessage"
  | "conversationFollowUpEnabled"
  | "conversationFollowUps"
  | "enabledAi"
  | "aiFunctions"
  | "enabledAiSkillSlugs"
  | "customerSheetSettings"
  | "appName"
>;

const createDefaultFollowUp = (): TenantConversationFollowUp => ({
  minutes: DEFAULT_FOLLOW_UP_MINUTES,
  isTemplate: false,
  text: "",
  templateDataKeys: [],
  templateParams: [],
  suggestNearestAppointment: false,
  closeAfterSend: false,
  ignoreIfScheduled: false,
});

const normalizeFollowUpForState = (
  followUp?: TenantConversationFollowUp,
): TenantConversationFollowUp => {
  const minutes = Number(followUp?.minutes);
  return {
    minutes:
      Number.isFinite(minutes) && minutes > 0
        ? minutes
        : DEFAULT_FOLLOW_UP_MINUTES,
    isTemplate: followUp?.isTemplate === true,
    text: typeof followUp?.text === "string" ? followUp.text : "",
    templateId:
      typeof followUp?.templateId === "string" ? followUp.templateId : "",
    templateDataKeys: normalizeStringArray(followUp?.templateDataKeys),
    templateParams: normalizeStringArray(followUp?.templateParams),
    suggestNearestAppointment: followUp?.suggestNearestAppointment === true,
    closeAfterSend: followUp?.closeAfterSend === true,
    ignoreIfScheduled: followUp?.ignoreIfScheduled === true,
  };
};

export default function TenantAiConfig() {
  const { user } = useAuth();
  const { error, success } = useToast();
  const canAccess = useMemo(
    () => AccessControl.canView(APP_RESOURCES.CONFIG_AI_TENANT, user?.role),
    [user?.role],
  );
  const [tenant, setTenant] = useState<AiConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [initialTenantSnapshot, setInitialTenantSnapshot] =
    useState<AiConfig | null>(null);
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [aiModelOptions, setAiModelOptions] = useState<ModelOption[]>([]);
  const [isLoadingAiModels, setIsLoadingAiModels] = useState(false);
  const [aiFunctionOptions, setAiFunctionOptions] = useState<
    AiFunctionOption[]
  >([]);
  const [isLoadingAiFunctions, setIsLoadingAiFunctions] = useState(false);
  const [aiSkillOptions, setAiSkillOptions] = useState<AiSkillOption[]>([]);
  const [isLoadingAiSkills, setIsLoadingAiSkills] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const [selectedTenantAppName, setSelectedTenantAppName] = useState("");
  const [pendingTenantAppName, setPendingTenantAppName] = useState("");
  const [isSwitchTenantModalOpen, setIsSwitchTenantModalOpen] = useState(false);
  const promptTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const promptLineNumbersRef = useRef<HTMLDivElement | null>(null);
  const [customerDataDictionary, setCustomerDataDictionary] = useState<
    TenantCustomerDataDictionaryItem[]
  >(DEFAULT_CUSTOMER_DATA_DICTIONARY);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const promptValue = tenant?.prompt ?? "";
  const promptLineCount = useMemo(() => {
    if (promptValue.length === 0) return 1;
    return promptValue.split(/\r\n|\r|\n/).length;
  }, [promptValue]);
  const promptWordCount = useMemo(() => {
    const trimmedPrompt = promptValue.trim();
    if (trimmedPrompt.length === 0) return 0;
    return trimmedPrompt.split(/\s+/).length;
  }, [promptValue]);
  const promptLineNumbers = useMemo(
    () =>
      Array.from({ length: promptLineCount }, (_, index) =>
        String(index + 1),
      ).join("\n"),
    [promptLineCount],
  );
  const promptCharacterCount = promptValue.length;
  const allAiFunctions = useMemo(
    () => aiFunctionOptions.map((option) => option.value),
    [aiFunctionOptions],
  );
  const allAiSkillSlugs = useMemo(
    () => aiSkillOptions.map((option) => option.value),
    [aiSkillOptions],
  );
  const selectedAiSkillSlugs = useMemo(() => {
    const selected = normalizeStringArray(tenant?.enabledAiSkillSlugs);
    if (allAiSkillSlugs.length === 0) return selected;
    const available = new Set(allAiSkillSlugs);
    return selected.filter((slug) => available.has(slug));
  }, [allAiSkillSlugs, tenant?.enabledAiSkillSlugs]);
  const selectedAiSkillsCount = selectedAiSkillSlugs.length;
  const selectedAiSkillOptions = useMemo(
    () =>
      aiSkillOptions.filter((option) =>
        selectedAiSkillSlugs.includes(option.value),
      ),
    [aiSkillOptions, selectedAiSkillSlugs],
  );
  const availableTenantAppNames = useMemo(
    () =>
      Array.from(
        new Set(
          tenants
            .map((item) => item.appName?.trim())
            .filter((appName): appName is string => !!appName),
        ),
      ).sort((left, right) => left.localeCompare(right, "pt-BR")),
    [tenants],
  );
  const isFormDirty = useMemo(() => {
    if (!tenant || !initialTenantSnapshot) return false;
    return JSON.stringify(tenant) !== JSON.stringify(initialTenantSnapshot);
  }, [tenant, initialTenantSnapshot]);
  const aiModelOptionsWithSelected = useMemo(() => {
    const options = [...aiModelOptions];
    const existing = new Set(options.map((option) => option.value));
    const selectedModels = [
      tenant?.gptModel,
      tenant?.gptImageModel,
      tenant?.gptAudioModel,
      tenant?.gptFileModel,
    ];

    selectedModels.forEach((model) => {
      const normalized = model?.trim();
      if (!normalized || existing.has(normalized)) return;
      existing.add(normalized);
      options.push({ value: normalized, label: normalized });
    });

    return options;
  }, [
    aiModelOptions,
    tenant?.gptAudioModel,
    tenant?.gptFileModel,
    tenant?.gptImageModel,
    tenant?.gptModel,
  ]);

  const resolveTemplateParamLimit = (templateId?: string): number | null => {
    if (!templateId) return null;

    const selectedTemplate = templateOptions.find(
      (template) => template.id === templateId,
    );
    const parsed = Number(selectedTemplate?.params_count);

    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }

    return Math.floor(parsed);
  };

  useEffect(() => {
    if (!canAccess) {
      setTenants([]);
      setSelectedTenantAppName("");
      setIsLoadingTenants(false);
      return;
    }

    let isMounted = true;

    const fetchTenants = async () => {
      try {
        setIsLoadingTenants(true);

        const pageSize = 100;
        let pageNumber = 1;
        let totalPages = 1;
        const allTenants: Tenant[] = [];

        while (pageNumber <= totalPages) {
          const response = await api.get<FindAllTenantDto>("/tenant", {
            params: { page: pageNumber, limit: pageSize },
          });

          allTenants.push(...response.data.data);
          totalPages = response.data.totalPages;
          pageNumber += 1;
        }

        const uniqueTenants = Array.from(
          new Map(allTenants.map((item) => [item.appName, item])).values(),
        );

        if (!isMounted) return;
        setTenants(uniqueTenants);
        setSelectedTenantAppName((previous) => {
          if (
            previous &&
            uniqueTenants.some((item) => item.appName === previous)
          ) {
            return previous;
          }

          if (
            user?.appName &&
            uniqueTenants.some((item) => item.appName === user.appName)
          ) {
            return user.appName;
          }

          return uniqueTenants[0]?.appName ?? "";
        });
      } catch (err) {
        if (!isMounted) return;
        error(getApiErrorMessage(err));
        setTenants([]);
        setSelectedTenantAppName("");
      } finally {
        if (!isMounted) return;
        setIsLoadingTenants(false);
      }
    };

    void fetchTenants();

    return () => {
      isMounted = false;
    };
  }, [canAccess, error, user?.appName]);

  useEffect(() => {
    if (!canAccess) {
      setAiModelOptions([]);
      setIsLoadingAiModels(false);
      return;
    }

    let isMounted = true;

    const fetchAiModels = async () => {
      try {
        setIsLoadingAiModels(true);
        const response = await api.get<AiModel[]>("/ai-models");
        if (!isMounted) return;

        const options = (response.data ?? [])
          .filter((item): item is AiModel => {
            return (
              !!item &&
              typeof item.model === "string" &&
              item.model.trim().length > 0
            );
          })
          .map((item) => ({
            value: item.model.trim(),
            label: item.vendor?.trim()
              ? `${item.model.trim()} · ${item.vendor.trim()}`
              : item.model.trim(),
          }));
        setAiModelOptions(options);
      } catch (err) {
        if (!isMounted) return;
        setAiModelOptions([]);
        error(getApiErrorMessage(err));
      } finally {
        if (!isMounted) return;
        setIsLoadingAiModels(false);
      }
    };

    void fetchAiModels();

    return () => {
      isMounted = false;
    };
  }, [canAccess, error]);

  useEffect(() => {
    if (!canAccess) {
      setAiFunctionOptions([]);
      setIsLoadingAiFunctions(false);
      return;
    }

    let isMounted = true;

    const fetchAiFunctions = async () => {
      try {
        setIsLoadingAiFunctions(true);
        const response = await api.get<unknown>("/tenant/ai-functions");
        if (!isMounted) return;

        setAiFunctionOptions(normalizeAiFunctionOptions(response.data));
      } catch (err) {
        if (!isMounted) return;
        setAiFunctionOptions([]);
        error(getApiErrorMessage(err));
      } finally {
        if (!isMounted) return;
        setIsLoadingAiFunctions(false);
      }
    };

    void fetchAiFunctions();

    return () => {
      isMounted = false;
    };
  }, [canAccess, error]);

  useEffect(() => {
    if (!canAccess) {
      setAiSkillOptions([]);
      setIsLoadingAiSkills(false);
      return;
    }

    let isMounted = true;

    const fetchAiSkills = async () => {
      try {
        setIsLoadingAiSkills(true);
        const response =
          await api.get<AiSkillCatalogItem[]>("/tenant/ai-skills");
        if (!isMounted) return;

        setAiSkillOptions(normalizeAiSkillOptions(response.data));
      } catch (err) {
        if (!isMounted) return;
        setAiSkillOptions([]);
        error(getApiErrorMessage(err));
      } finally {
        if (!isMounted) return;
        setIsLoadingAiSkills(false);
      }
    };

    void fetchAiSkills();

    return () => {
      isMounted = false;
    };
  }, [canAccess, error]);

  useEffect(() => {
    if (!selectedTenantAppName) {
      setTenant(null);
      setInitialTenantSnapshot(null);
      setTemplateOptions([]);
      setIsLoading(false);
      return;
    }

    const fetchTenant = async () => {
      try {
        setIsLoading(true);
        const [tenantResponse, dictionaryResponse] = await Promise.all([
          api.get<Tenant>(`/tenant/${selectedTenantAppName}`),
          api
            .get<TenantCustomerDataDictionaryItem[]>(
              "/tenant/customer-data-dictionary",
            )
            .catch(() => null),
        ]);

        const data = tenantResponse.data;

        const loadedFollowUps = Array.isArray(data.conversationFollowUps)
          ? data.conversationFollowUps.map((followUp) =>
              normalizeFollowUpForState(followUp),
            )
          : [];
        const conversationFollowUps =
          data.conversationFollowUpEnabled && loadedFollowUps.length === 0
            ? [createDefaultFollowUp()]
            : loadedFollowUps;
        const dictionaryFromEndpoint = normalizeCustomerDataDictionary(
          dictionaryResponse?.data,
        );
        const dictionaryFromTenant = normalizeCustomerDataDictionary(
          data.customerDataDictionary,
        );
        const dictionary =
          dictionaryFromEndpoint.length > 0
            ? dictionaryFromEndpoint
            : dictionaryFromTenant.length > 0
              ? dictionaryFromTenant
              : DEFAULT_CUSTOMER_DATA_DICTIONARY;

        const nextTenant: AiConfig = {
          appName: data.appName,
          enabledAi: data.enabledAi,
          aiFunctions: normalizeAiFunctions(data.aiFunctions, allAiFunctions),
          enabledAiSkillSlugs:
            allAiSkillSlugs.length > 0
              ? normalizeStringArray(data.enabledAiSkillSlugs).filter((slug) =>
                  allAiSkillSlugs.includes(slug),
                )
              : normalizeStringArray(data.enabledAiSkillSlugs),
          prompt: data.prompt ?? "",
          gptModel: data.gptModel,
          gptAudioModel: data.gptAudioModel ?? "gpt-4o-transcribe",
          gptImageModel: data.gptImageModel ?? "gpt-5.4-mini",
          gptFileModel: data.gptFileModel ?? "gpt-5.4-mini",
          enabledAiAudioInterpretation:
            data.enabledAiAudioInterpretation ?? true,
          enabledAiImageInterpretation:
            data.enabledAiImageInterpretation ?? true,
          enabledAiFileInterpretation:
            data.enabledAiFileInterpretation ?? false,
          enabledAssistantMessageSplit:
            data.enabledAssistantMessageSplit ?? true,
          conversationTimeoutEnabled: !!data.conversationTimeoutEnabled,
          conversationTimeoutMinutes: data.conversationTimeoutMinutes ?? 5,
          conversationTimeoutMessage:
            data.conversationTimeoutMessage ?? DEFAULT_TIMEOUT_MESSAGE,
          conversationFollowUpEnabled: !!data.conversationFollowUpEnabled,
          conversationFollowUps,
          customerSheetSettings: data.customerSheetSettings,
        };

        setCustomerDataDictionary(dictionary);
        setTenant(nextTenant);
        setInitialTenantSnapshot(nextTenant);
      } catch (err) {
        error(getApiErrorMessage(err));
        setTenant(null);
        setInitialTenantSnapshot(null);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchTenant();
  }, [selectedTenantAppName, error, allAiFunctions, allAiSkillSlugs]);

  useEffect(() => {
    if (!selectedTenantAppName) return;

    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const response = await api.get<TemplateOption[]>(
          `/templates/${selectedTenantAppName}`,
        );
        setTemplateOptions(response.data ?? []);
      } catch (err) {
        setTemplateOptions([]);
        error(getApiErrorMessage(err));
      } finally {
        setTemplatesLoading(false);
      }
    };

    void fetchTemplates();
  }, [selectedTenantAppName, error]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    if (!tenant) return;
    const { name, value, type } = e.target;

    if (name === "conversationTimeoutMinutes") {
      const parsed = Number(value);
      setTenant({
        ...tenant,
        [name]: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined,
      });
      return;
    }

    if (type === "checkbox" && "checked" in e.target) {
      const checked = (e.target as HTMLInputElement).checked;

      if (name === "conversationFollowUpEnabled") {
        const currentFollowUps = tenant.conversationFollowUps ?? [];
        setTenant({
          ...tenant,
          conversationFollowUpEnabled: checked,
          conversationFollowUps: checked
            ? currentFollowUps.length === 0
              ? [createDefaultFollowUp()]
              : currentFollowUps
            : [],
        });
        return;
      }

      setTenant({ ...tenant, [name]: checked });
      return;
    }

    setTenant({ ...tenant, [name]: value });
  };

  const handleAiFunctionsChange = (functions: AiConfig["aiFunctions"]) => {
    setTenant((previous) =>
      previous
        ? {
            ...previous,
            aiFunctions: normalizeAiFunctions(functions, allAiFunctions),
          }
        : previous,
    );
  };

  const handleAiSkillsChange = (selectedOptions: MultiValue<AiSkillOption>) => {
    const selectedValues = selectedOptions.map((option) => option.value);

    setTenant((previous) =>
      previous
        ? {
            ...previous,
            enabledAiSkillSlugs: normalizeStringArray(selectedValues),
          }
        : previous,
    );
  };

  const selectAllAiSkills = () => {
    setTenant((previous) =>
      previous
        ? {
            ...previous,
            enabledAiSkillSlugs: [...allAiSkillSlugs],
          }
        : previous,
    );
  };

  const clearAiSkills = () => {
    setTenant((previous) =>
      previous
        ? {
            ...previous,
            enabledAiSkillSlugs: [],
          }
        : previous,
    );
  };

  const setPromptCursorPosition = (position: number) => {
    requestAnimationFrame(() => {
      const textarea = promptTextareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(position, position);
    });
  };

  const handlePromptChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const nextPrompt = event.target.value;
    setTenant((previous) =>
      previous
        ? {
            ...previous,
            prompt: nextPrompt,
          }
        : previous,
    );
  };

  const handlePromptScroll = (event: React.UIEvent<HTMLTextAreaElement>) => {
    if (!promptLineNumbersRef.current) return;
    promptLineNumbersRef.current.scrollTop = event.currentTarget.scrollTop;
  };

  const handlePromptKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key !== "Tab") return;
    event.preventDefault();

    const textarea = event.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const indentation = "  ";

    setTenant((previous) => {
      if (!previous) return previous;

      const currentPrompt = previous.prompt ?? "";
      const nextPrompt = `${currentPrompt.slice(0, start)}${indentation}${currentPrompt.slice(end)}`;
      setPromptCursorPosition(start + indentation.length);

      return {
        ...previous,
        prompt: nextPrompt,
      };
    });
  };

  const insertPromptSnippet = (snippet: string) => {
    const textarea = promptTextareaRef.current;

    setTenant((previous) => {
      if (!previous) return previous;

      const currentPrompt = previous.prompt ?? "";
      const start = textarea?.selectionStart ?? currentPrompt.length;
      const end = textarea?.selectionEnd ?? currentPrompt.length;
      const nextPrompt = `${currentPrompt.slice(0, start)}${snippet}${currentPrompt.slice(end)}`;
      setPromptCursorPosition(start + snippet.length);

      return {
        ...previous,
        prompt: nextPrompt,
      };
    });
  };

  const handleFollowUpChange = (
    index: number,
    updates: Partial<TenantConversationFollowUp>,
  ) => {
    setTenant((previous) => {
      if (!previous) return previous;

      const followUps = [...(previous.conversationFollowUps ?? [])];
      const current = normalizeFollowUpForState(followUps[index]);
      const updated: TenantConversationFollowUp = {
        ...current,
        ...updates,
      };

      if (updates.isTemplate === true) {
        updated.text = "";
      }

      if (updates.isTemplate === false) {
        updated.templateId = "";
        updated.templateDataKeys = [];
        updated.templateParams = [];
        updated.suggestNearestAppointment = false;
      }

      if (typeof updates.templateId === "string") {
        const paramLimit = resolveTemplateParamLimit(updates.templateId);
        if (paramLimit !== null) {
          if (paramLimit === 0) {
            updated.suggestNearestAppointment = false;
          }
          const reservedSlots = updated.suggestNearestAppointment ? 1 : 0;
          const maxCustomerDataSlots = Math.max(0, paramLimit - reservedSlots);
          updated.templateDataKeys = normalizeStringArray(
            updated.templateDataKeys,
          ).slice(0, maxCustomerDataSlots);
        }
      }

      if (typeof updates.suggestNearestAppointment === "boolean") {
        const paramLimit = resolveTemplateParamLimit(updated.templateId);
        if (paramLimit !== null) {
          if (updates.suggestNearestAppointment && paramLimit === 0) {
            updated.suggestNearestAppointment = false;
          }

          const reservedSlots = updated.suggestNearestAppointment ? 1 : 0;
          const maxCustomerDataSlots = Math.max(0, paramLimit - reservedSlots);
          updated.templateDataKeys = normalizeStringArray(
            updated.templateDataKeys,
          ).slice(0, maxCustomerDataSlots);
        }
      }

      followUps[index] = updated;

      return {
        ...previous,
        conversationFollowUps: followUps,
      };
    });
  };

  const addFollowUp = () => {
    setTenant((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        conversationFollowUps: [
          ...(previous.conversationFollowUps ?? []),
          createDefaultFollowUp(),
        ],
      };
    });
  };

  const removeFollowUp = (index: number) => {
    setTenant((previous) => {
      if (!previous) return previous;
      const currentFollowUps = previous.conversationFollowUps ?? [];
      if (currentFollowUps.length <= 1) return previous;

      return {
        ...previous,
        conversationFollowUps: currentFollowUps.filter((_, i) => i !== index),
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant || !tenant.appName || isSaving) return;

    if (tenant.conversationTimeoutEnabled) {
      const timeoutMinutes = Number(tenant.conversationTimeoutMinutes);

      if (!Number.isFinite(timeoutMinutes) || timeoutMinutes <= 0) {
        error(
          "Informe os minutos do encerramento por ociosidade quando ele estiver habilitado.",
        );
        return;
      }
    }

    const dictionaryKeys = new Set(
      customerDataDictionary.map((item) => item.key),
    );
    const normalizedFollowUps = (tenant.conversationFollowUps ?? []).map(
      (followUp) => {
        const normalized = normalizeFollowUpForState(followUp);

        if (!normalized.isTemplate) {
          return {
            ...normalized,
            templateId: "",
            templateDataKeys: [],
            templateParams: [],
            suggestNearestAppointment: false,
          };
        }

        return {
          ...normalized,
          text: "",
          templateId: normalized.templateId?.trim() ?? "",
          templateDataKeys: normalizeStringArray(normalized.templateDataKeys)
            .filter((key) => dictionaryKeys.has(key))
            .slice(
              0,
              Math.max(
                0,
                (resolveTemplateParamLimit(normalized.templateId ?? "") ??
                  Number.MAX_SAFE_INTEGER) -
                  (normalized.suggestNearestAppointment ? 1 : 0),
              ),
            ),
          // O campo legado deixa de ser enviado pela UI.
          templateParams: [],
        };
      },
    );
    const followUpsToSend = tenant.conversationFollowUpEnabled
      ? normalizedFollowUps
      : [];
    const aiFunctionsToSend = normalizeAiFunctions(
      tenant.aiFunctions,
      allAiFunctions,
    );
    const shouldSendAiFunctions =
      allAiFunctions.length > 0 || Array.isArray(tenant.aiFunctions);
    const enabledAiSkillSlugsToSend = normalizeStringArray(
      tenant.enabledAiSkillSlugs,
    );

    if (tenant.conversationFollowUpEnabled) {
      if (followUpsToSend.length === 0) {
        error(
          "Adicione ao menos um follow-up por ociosidade quando ele estiver habilitado.",
        );
        return;
      }

      const timeoutMinutes = Number(tenant.conversationTimeoutMinutes);

      for (let index = 0; index < followUpsToSend.length; index += 1) {
        const followUp = followUpsToSend[index];

        if (!Number.isFinite(followUp.minutes) || followUp.minutes <= 0) {
          error(`Informe minutos válidos no follow-up ${index + 1}.`);
          return;
        }

        if (
          tenant.conversationTimeoutEnabled &&
          (!Number.isFinite(timeoutMinutes) ||
            followUp.minutes >= timeoutMinutes)
        ) {
          error(
            `O follow-up ${index + 1} deve ser menor do que o tempo de encerramento por ociosidade.`,
          );
          return;
        }

        if (followUp.isTemplate) {
          if (!followUp.templateId || followUp.templateId.trim().length === 0) {
            error(`Selecione o template do follow-up ${index + 1}.`);
            return;
          }

          const templateParamLimit = resolveTemplateParamLimit(
            followUp.templateId,
          );
          if (templateParamLimit === null) {
            error(
              `Não foi possível validar a quantidade de parâmetros do template no follow-up ${index + 1}.`,
            );
            return;
          }

          if (followUp.suggestNearestAppointment && templateParamLimit < 1) {
            error(
              `O template do follow-up ${index + 1} não possui parâmetros para sugerir horário.`,
            );
            return;
          }

          const selectedParamCount =
            normalizeStringArray(followUp.templateDataKeys).length +
            (followUp.suggestNearestAppointment ? 1 : 0);
          if (selectedParamCount !== templateParamLimit) {
            error(
              `O follow-up ${index + 1} deve preencher exatamente ${templateParamLimit} parâmetro(s) do template. Atualmente: ${selectedParamCount}.`,
            );
            return;
          }
        } else {
          if (!followUp.text || followUp.text.trim().length === 0) {
            error(`Informe a mensagem de texto do follow-up ${index + 1}.`);
            return;
          }
        }
      }
    }

    try {
      setIsSaving(true);
      await api.patch(`/tenant/${tenant.appName}`, {
        prompt: tenant.prompt,
        gptModel: tenant.gptModel,
        gptAudioModel: tenant.gptAudioModel,
        gptImageModel: tenant.gptImageModel,
        gptFileModel: tenant.gptFileModel,
        ...(shouldSendAiFunctions ? { aiFunctions: aiFunctionsToSend } : {}),
        enabledAiSkillSlugs: enabledAiSkillSlugsToSend,
        enabledAiAudioInterpretation: tenant.enabledAiAudioInterpretation,
        enabledAiImageInterpretation: tenant.enabledAiImageInterpretation,
        enabledAiFileInterpretation: tenant.enabledAiFileInterpretation,
        enabledAssistantMessageSplit: tenant.enabledAssistantMessageSplit,
        conversationTimeoutEnabled: tenant.conversationTimeoutEnabled,
        conversationTimeoutMinutes: tenant.conversationTimeoutMinutes,
        conversationTimeoutMessage: tenant.conversationTimeoutMessage,
        conversationFollowUpEnabled: tenant.conversationFollowUpEnabled,
        conversationFollowUps: followUpsToSend,
        customerSheetSettings:
          tenant.customerSheetSettings?.enabled ||
          tenant.customerSheetSettings?.spreadsheetId
            ? tenant.customerSheetSettings
            : undefined,
      });
      setInitialTenantSnapshot(tenant);
      success("Configurações de IA atualizadas com sucesso!");
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTenantSelectionChange = (nextTenantAppName: string) => {
    if (
      !nextTenantAppName ||
      nextTenantAppName === selectedTenantAppName ||
      isSaving
    ) {
      return;
    }

    if (isFormDirty) {
      setPendingTenantAppName(nextTenantAppName);
      setIsSwitchTenantModalOpen(true);
      return;
    }

    setSelectedTenantAppName(nextTenantAppName);
  };

  const confirmTenantSwitch = () => {
    if (!pendingTenantAppName) return;
    setSelectedTenantAppName(pendingTenantAppName);
    setPendingTenantAppName("");
    setIsSwitchTenantModalOpen(false);
  };

  const cancelTenantSwitch = () => {
    setPendingTenantAppName("");
    setIsSwitchTenantModalOpen(false);
  };

  const tenantSelector = (
    <div className="max-w-md">
      <label className="form-label">Tenant</label>
      <select
        className="form-input"
        value={selectedTenantAppName}
        onChange={(event) => handleTenantSelectionChange(event.target.value)}
        disabled={isLoadingTenants || isLoading || isSaving}
      >
        {isLoadingTenants && <option value="">Carregando...</option>}
        {!isLoadingTenants && availableTenantAppNames.length === 0 && (
          <option value="">Nenhum tenant disponível</option>
        )}
        {!isLoadingTenants &&
          availableTenantAppNames.map((appName) => (
            <option key={appName} value={appName}>
              {appName}
            </option>
          ))}
      </select>
    </div>
  );

  if (!canAccess) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-2">Configurações de IA</h1>
        <p className="app-page-subtitle">
          Você não tem permissão para acessar esta página.
        </p>
      </div>
    );
  }

  if (isLoadingTenants || isLoading) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de IA do tenant</h1>
        {tenantSelector}
        <p className="app-page-subtitle">Carregando configurações...</p>
      </div>
    );
  }

  if (!selectedTenantAppName) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de IA do tenant</h1>
        {tenantSelector}
        <p className="app-page-subtitle mt-4">
          Nenhum tenant disponível para edição.
        </p>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de IA do tenant</h1>
        {tenantSelector}
        <p className="app-page-subtitle">
          Não foi possível carregar as configurações do tenant.
        </p>
      </div>
    );
  }

  if (!tenant.enabledAi) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de IA do tenant</h1>
        {tenantSelector}
        <p className="app-page-subtitle mt-4">
          A IA está desabilitada para este tenant.
        </p>
      </div>
    );
  }

  return (
    <div className="app-page">
      <BackButton />
      <h1 className="app-page-title mb-6">Configurações de IA do tenant</h1>
      {tenantSelector}

      <form onSubmit={handleSubmit} className="space-y-6 mt-6">
        <div>
          <label className="form-label">Modelo GPT</label>
          <Select<ModelOption, false>
            name="gptModel"
            options={aiModelOptionsWithSelected}
            value={
              tenant.gptModel
                ? { value: tenant.gptModel, label: tenant.gptModel }
                : null
            }
            onChange={(option) =>
              setTenant((previous) =>
                previous
                  ? {
                      ...previous,
                      gptModel: option?.value ?? "",
                    }
                  : previous,
              )
            }
            isLoading={isLoadingAiModels}
            isSearchable
            className="text-sm"
            classNamePrefix="ai-model-select"
            placeholder="Selecione o modelo principal..."
            noOptionsMessage={() => "Nenhum modelo encontrado"}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="enabledAssistantMessageSplit"
            checked={!!tenant.enabledAssistantMessageSplit}
            onChange={handleChange}
            className="form-checkbox"
          />
          <label className="text-sm text-slate-700">
            Enviar respostas em mensagens separadas por bloco
          </label>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="enabledAiAudioInterpretation"
            checked={!!tenant.enabledAiAudioInterpretation}
            onChange={handleChange}
            className="form-checkbox"
          />
          <label className="text-sm text-slate-700">Entender áudio</label>
        </div>
        {!!tenant.enabledAiAudioInterpretation && (
          <div>
            <label className="form-label">Modelo para áudio</label>
            <Select<ModelOption, false>
              name="gptAudioModel"
              options={aiModelOptionsWithSelected}
              value={
                tenant.gptAudioModel
                  ? { value: tenant.gptAudioModel, label: tenant.gptAudioModel }
                  : null
              }
              onChange={(option) =>
                setTenant((previous) =>
                  previous
                    ? {
                        ...previous,
                        gptAudioModel: option?.value ?? "",
                      }
                    : previous,
                )
              }
              isLoading={isLoadingAiModels}
              isSearchable
              className="text-sm"
              classNamePrefix="ai-model-select"
              placeholder="Selecione o modelo de áudio..."
              noOptionsMessage={() => "Nenhum modelo encontrado"}
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="enabledAiImageInterpretation"
            checked={!!tenant.enabledAiImageInterpretation}
            onChange={handleChange}
            className="form-checkbox"
          />
          <label className="text-sm text-slate-700">Entender imagem</label>
        </div>
        {!!tenant.enabledAiImageInterpretation && (
          <div>
            <label className="form-label">Modelo para imagem</label>
            <Select<ModelOption, false>
              name="gptImageModel"
              options={aiModelOptionsWithSelected}
              value={
                tenant.gptImageModel
                  ? { value: tenant.gptImageModel, label: tenant.gptImageModel }
                  : null
              }
              onChange={(option) =>
                setTenant((previous) =>
                  previous
                    ? {
                        ...previous,
                        gptImageModel: option?.value ?? "",
                      }
                    : previous,
                )
              }
              isLoading={isLoadingAiModels}
              isSearchable
              className="text-sm"
              classNamePrefix="ai-model-select"
              placeholder="Selecione o modelo de imagem..."
              noOptionsMessage={() => "Nenhum modelo encontrado"}
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="enabledAiFileInterpretation"
            checked={!!tenant.enabledAiFileInterpretation}
            onChange={handleChange}
            className="form-checkbox"
          />
          <label className="text-sm text-slate-700">Entender documentos</label>
        </div>
        {!!tenant.enabledAiFileInterpretation && (
          <div>
            <label className="form-label">Modelo para documentos</label>
            <Select<ModelOption, false>
              name="gptFileModel"
              options={aiModelOptionsWithSelected}
              value={
                tenant.gptFileModel
                  ? { value: tenant.gptFileModel, label: tenant.gptFileModel }
                  : null
              }
              onChange={(option) =>
                setTenant((previous) =>
                  previous
                    ? {
                        ...previous,
                        gptFileModel: option?.value ?? "",
                      }
                    : previous,
                )
              }
              isLoading={isLoadingAiModels}
              isSearchable
              className="text-sm"
              classNamePrefix="ai-model-select"
              placeholder="Selecione o modelo de documentos..."
              noOptionsMessage={() => "Nenhum modelo encontrado"}
            />
          </div>
        )}

        <AiFunctionsField
          options={aiFunctionOptions}
          value={tenant.aiFunctions}
          onChange={handleAiFunctionsChange}
          isLoading={isLoadingAiFunctions}
        />

        <section className="app-card space-y-4 p-4">
          <h2 className="app-section-title">Consulta cadastral em planilha</h2>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!tenant.customerSheetSettings?.enabled}
              onChange={(event) =>
                setTenant({
                  ...tenant,
                  customerSheetSettings: {
                    spreadsheetId: "",
                    sheetName: "",
                    lookupColumn: "",
                    ...tenant.customerSheetSettings,
                    enabled: event.target.checked,
                  },
                })
              }
              className="form-checkbox"
            />
            <span className="text-sm text-slate-700">
              Habilitar consulta por código
            </span>
          </label>
          {tenant.customerSheetSettings?.enabled && (
            <>
              <p className="text-sm text-slate-500">
                O código informado autoriza a consulta. Todas as colunas serão
                disponibilizadas ao atendimento; use somente dados aprovados.
                Compartilhe a planilha com a conta de serviço configurada no
                servidor.
              </p>
              {(["spreadsheetId", "sheetName", "lookupColumn"] as const).map(
                (key, index) => (
                  <label className="form-label block" key={key}>
                    {
                      ["ID da planilha", "Aba", "Cabeçalho da coluna de busca"][
                        index
                      ]
                    }
                    <input
                      className="form-input"
                      required
                      maxLength={200}
                      value={tenant.customerSheetSettings?.[key] || ""}
                      onChange={(event) =>
                        setTenant({
                          ...tenant,
                          customerSheetSettings: {
                            ...tenant.customerSheetSettings!,
                            [key]: event.target.value,
                          },
                        })
                      }
                    />
                  </label>
                ),
              )}
            </>
          )}
        </section>

        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <label className="form-label mb-0">Skills de IA</label>
              <p className="text-xs text-slate-500">
                Selecione quais módulos efêmeros podem ser roteados para este
                tenant.
              </p>
            </div>
            <p className="text-xs text-slate-600">
              Selecionadas: {selectedAiSkillsCount} de {allAiSkillSlugs.length}
            </p>
          </div>

          <div>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <label className="form-label mb-0">Skills disponíveis</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllAiSkills}
                  disabled={isLoadingAiSkills || allAiSkillSlugs.length === 0}
                  className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Todas
                </button>
                <button
                  type="button"
                  onClick={clearAiSkills}
                  disabled={isLoadingAiSkills}
                  className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Nenhuma
                </button>
              </div>
            </div>
            <Select<AiSkillOption, true>
              isMulti
              closeMenuOnSelect={false}
              options={aiSkillOptions}
              value={selectedAiSkillOptions}
              onChange={handleAiSkillsChange}
              formatOptionLabel={formatAiSkillOptionLabel}
              isLoading={isLoadingAiSkills}
              isDisabled={isLoadingAiSkills}
              classNamePrefix="ai-skills-select"
              className="text-sm"
              placeholder="Selecione as skills..."
              loadingMessage={() => "Carregando skills..."}
              noOptionsMessage={() => "Nenhuma skill encontrada"}
            />
            <p className="mt-1 text-xs text-slate-500">
              Skills são inseridas apenas no payload atual da IA e não são
              salvas no histórico da conversa.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <label className="form-label mb-0">Prompt</label>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              Editor avançado
            </span>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/80 px-3 py-2">
              <p className="text-xs text-slate-600">
                Monte seu prompt com blocos prontos e mantenha um padrão de
                escrita.
              </p>
              <div className="flex flex-wrap gap-2">
                {PROMPT_SNIPPETS.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => insertPromptSnippet(item.snippet)}
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid h-[24rem] grid-cols-[3.5rem_1fr]">
              <div
                ref={promptLineNumbersRef}
                className="overflow-hidden border-r border-slate-200 bg-slate-50/90"
              >
                <pre className="select-none px-3 py-3 text-right font-mono text-xs leading-6 text-slate-400">
                  {promptLineNumbers}
                </pre>
              </div>

              <textarea
                ref={promptTextareaRef}
                name="prompt"
                value={promptValue}
                onChange={handlePromptChange}
                onScroll={handlePromptScroll}
                onKeyDown={handlePromptKeyDown}
                spellCheck={false}
                className="h-full w-full resize-none border-0 bg-white px-4 py-3 font-mono text-sm leading-6 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                placeholder="Escreva aqui as instruções de papel, contexto, regras e formato de resposta da IA..."
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/80 px-3 py-2">
              <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
                <span>
                  {promptLineCount} {promptLineCount === 1 ? "linha" : "linhas"}
                </span>
                <span>
                  {promptWordCount}{" "}
                  {promptWordCount === 1 ? "palavra" : "palavras"}
                </span>
                <span>
                  {promptCharacterCount}{" "}
                  {promptCharacterCount === 1 ? "caractere" : "caracteres"}
                </span>
              </div>
              <span className="text-xs text-slate-500">
                Atalho: pressione Tab para inserir indentação.
              </span>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex items-center gap-2 md:pt-7">
            <input
              type="checkbox"
              name="conversationTimeoutEnabled"
              checked={!!tenant.conversationTimeoutEnabled}
              onChange={handleChange}
              className="form-checkbox"
            />
            <label className="text-sm text-slate-700">
              Encerrar por ociosidade
            </label>
          </div>

          <div className="flex items-center gap-2 md:pt-7">
            <input
              type="checkbox"
              name="conversationFollowUpEnabled"
              checked={!!tenant.conversationFollowUpEnabled}
              onChange={handleChange}
              className="form-checkbox"
            />
            <label className="text-sm text-slate-700">Enviar follow-up</label>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {tenant.conversationTimeoutEnabled && (
            <>
              <div>
                <label className="form-label">
                  Encerrar por ociosidade (min)
                </label>
                <input
                  type="number"
                  name="conversationTimeoutMinutes"
                  min={1}
                  value={tenant.conversationTimeoutMinutes ?? ""}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">Mensagem de encerramento</label>
                <textarea
                  name="conversationTimeoutMessage"
                  value={tenant.conversationTimeoutMessage ?? ""}
                  onChange={handleChange}
                  rows={3}
                  className="form-textarea resize-none"
                  placeholder="Mensagem enviada quando a conversa é encerrada por ociosidade."
                />
              </div>
            </>
          )}
        </div>

        {tenant.conversationFollowUpEnabled && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-800">
                Follow-ups por ociosidade
              </h2>
              <button
                type="button"
                onClick={addFollowUp}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
              >
                Adicionar follow-up
              </button>
            </div>

            {templatesLoading && (
              <p className="text-xs text-slate-500">Carregando templates...</p>
            )}

            {!templatesLoading &&
              (tenant.conversationFollowUps ?? []).map((followUp, index) => {
                const current = normalizeFollowUpForState(followUp);
                const templateParamLimit = resolveTemplateParamLimit(
                  current.templateId,
                );
                const isTemplateSelected =
                  typeof current.templateId === "string" &&
                  current.templateId.trim().length > 0;
                const selectedTemplateDataKeys = normalizeStringArray(
                  current.templateDataKeys,
                );
                const selectedTemplateDataCount =
                  selectedTemplateDataKeys.length;
                const suggestedParamCount = current.suggestNearestAppointment
                  ? 1
                  : 0;
                const maxTemplateDataKeys =
                  templateParamLimit === null
                    ? null
                    : Math.max(0, templateParamLimit - suggestedParamCount);
                const suggestOptionDisabled =
                  !isTemplateSelected || templateParamLimit === 0;

                return (
                  <div
                    key={`${index}-${current.isTemplate ? "template" : "text"}`}
                    className="space-y-4 rounded-lg border border-slate-200 bg-white p-4"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-800">
                        Follow-up {index + 1}
                      </p>
                      <button
                        type="button"
                        onClick={() => removeFollowUp(index)}
                        disabled={
                          (tenant.conversationFollowUps ?? []).length <= 1
                        }
                        className="text-xs font-medium text-rose-600 disabled:cursor-not-allowed disabled:text-slate-400"
                      >
                        Remover
                      </button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="form-label">Disparo (min)</label>
                        <input
                          type="number"
                          min={1}
                          value={current.minutes}
                          onChange={(event) => {
                            const parsed = Number(event.target.value);
                            handleFollowUpChange(index, {
                              minutes:
                                Number.isFinite(parsed) && parsed > 0
                                  ? parsed
                                  : DEFAULT_FOLLOW_UP_MINUTES,
                            });
                          }}
                          className="form-input"
                        />
                      </div>

                      <div>
                        <label className="form-label">Tipo</label>
                        <select
                          value={current.isTemplate ? "template" : "text"}
                          onChange={(event) =>
                            handleFollowUpChange(index, {
                              isTemplate: event.target.value === "template",
                            })
                          }
                          className="form-input"
                        >
                          <option value="text">Texto</option>
                          <option value="template">Template</option>
                        </select>
                      </div>
                    </div>

                    {current.isTemplate ? (
                      <>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="form-label">Template</label>
                            <select
                              value={current.templateId ?? ""}
                              onChange={(event) =>
                                handleFollowUpChange(index, {
                                  templateId: event.target.value,
                                })
                              }
                              className="form-input"
                              disabled={templatesLoading || !tenant.appName}
                            >
                              <option value="">
                                {templatesLoading
                                  ? "Carregando templates..."
                                  : "Selecione um template"}
                              </option>
                              {templateOptions.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.element_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="form-label">
                              Parâmetros do template (dados do contato)
                            </label>
                            <div className="space-y-2 rounded-md border border-slate-300 p-3">
                              <label
                                className={`flex items-start gap-2 rounded-md border border-slate-200 p-2 ${
                                  suggestOptionDisabled ? "opacity-50" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!current.suggestNearestAppointment}
                                  disabled={suggestOptionDisabled}
                                  onChange={(event) =>
                                    handleFollowUpChange(index, {
                                      suggestNearestAppointment:
                                        event.target.checked,
                                    })
                                  }
                                  className="form-checkbox mt-0.5"
                                />
                                <span className="text-sm text-slate-700">
                                  Sugerir horário mais próximo no envio
                                  <span className="block text-xs text-slate-500">
                                    Esta opção usa 1 parâmetro do template.
                                  </span>
                                </span>
                              </label>

                              {customerDataDictionary.length > 0 ? (
                                customerDataDictionary.map((item) => {
                                  const selected =
                                    selectedTemplateDataKeys.includes(item.key);
                                  const limitReached =
                                    maxTemplateDataKeys !== null &&
                                    selectedTemplateDataCount >=
                                      maxTemplateDataKeys;
                                  const checkboxDisabled =
                                    !isTemplateSelected ||
                                    (!selected && limitReached);

                                  return (
                                    <label
                                      key={item.key}
                                      className={`flex items-start gap-2 ${
                                        checkboxDisabled ? "opacity-50" : ""
                                      } rounded-md border border-slate-200 p-2 ${
                                        checkboxDisabled
                                          ? "cursor-not-allowed"
                                          : ""
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        disabled={checkboxDisabled}
                                        onChange={(event) => {
                                          if (!isTemplateSelected) return;
                                          const currentKeys =
                                            normalizeStringArray(
                                              current.templateDataKeys,
                                            );
                                          if (
                                            event.target.checked &&
                                            maxTemplateDataKeys !== null &&
                                            currentKeys.length >=
                                              maxTemplateDataKeys
                                          ) {
                                            return;
                                          }
                                          const updatedKeys = event.target
                                            .checked
                                            ? [...currentKeys, item.key]
                                            : currentKeys.filter(
                                                (value) => value !== item.key,
                                              );

                                          handleFollowUpChange(index, {
                                            templateDataKeys:
                                              normalizeStringArray(updatedKeys),
                                            templateParams: [],
                                          });
                                        }}
                                        className="form-checkbox mt-0.5"
                                      />
                                      <span className="text-sm text-slate-700">
                                        {item.label}
                                        {item.description ? (
                                          <span className="block text-xs text-slate-500">
                                            {item.description}
                                          </span>
                                        ) : null}
                                      </span>
                                    </label>
                                  );
                                })
                              ) : (
                                <p className="text-xs text-slate-500">
                                  Nenhum item do dicionário disponível.
                                </p>
                              )}
                            </div>
                            <p className="mt-1 text-xs text-slate-500">
                              Selecione os campos do dicionário que serão
                              enviados como parâmetros do template.
                            </p>
                            {!isTemplateSelected && (
                              <p className="mt-1 text-xs text-slate-500">
                                Selecione um template para habilitar os
                                parâmetros.
                              </p>
                            )}
                            {templateParamLimit === 0 && isTemplateSelected && (
                              <p className="mt-1 text-xs text-slate-500">
                                Este template não possui parâmetros disponíveis.
                              </p>
                            )}
                            {templateParamLimit !== null && (
                              <p className="mt-1 text-xs text-slate-500">
                                Selecionados{" "}
                                {selectedTemplateDataCount +
                                  suggestedParamCount}{" "}
                                de {templateParamLimit} parâmetros permitidos
                                para este template.
                              </p>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div>
                        <label className="form-label">Mensagem de texto</label>
                        <textarea
                          value={current.text ?? ""}
                          onChange={(event) =>
                            handleFollowUpChange(index, {
                              text: event.target.value,
                            })
                          }
                          rows={3}
                          className="form-textarea resize-none"
                          placeholder="Mensagem enviada antes do encerramento por ociosidade."
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!current.ignoreIfScheduled}
                        onChange={(event) =>
                          handleFollowUpChange(index, {
                            ignoreIfScheduled: event.target.checked,
                          })
                        }
                        className="form-checkbox"
                      />
                      <label className="text-sm text-slate-700">
                        Ignorar se agendado
                      </label>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!current.closeAfterSend}
                        onChange={(event) =>
                          handleFollowUpChange(index, {
                            closeAfterSend: event.target.checked,
                          })
                        }
                        className="form-checkbox"
                      />
                      <label className="text-sm text-slate-700">
                        Encerrar conversa após enviar este follow-up
                      </label>
                    </div>
                  </div>
                );
              })}

            <p className="text-xs text-slate-500">
              O tempo de cada follow-up é contado após a última mensagem do
              contato.
            </p>
          </div>
        )}

        <LoadingButton
          type="submit"
          className="btn btn-neutral cursor-pointer"
          isLoading={isSaving}
          loadingLabel="Salvando"
        >
          Salvar alterações
        </LoadingButton>
      </form>

      <ConfirmationModal
        isOpen={isSwitchTenantModalOpen}
        title="Trocar tenant"
        description="Você possui alterações não salvas. Deseja descartar essas alterações e trocar de tenant?"
        onCancel={cancelTenantSwitch}
        onConfirm={confirmTenantSwitch}
        confirmLabel="Trocar tenant"
      />
    </div>
  );
}
